/**
 * @fileoverview Input/output schemas for the single-chapter-extractor-v2 agent.
 *
 * This agent extracts single-chapter metadata (events, goals, pacing, scenes)
 * from short-form fiction content.
 *
 * Note: Plot extraction (summary + writtenPlot) is handled separately by
 * plot-extractor-v2. This agent focuses only on chapter-level metadata.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { ChapterGoalsSchema, SceneImportSchema } from '@/domains/chapter-workspace/schemas';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

export const InputSchema = BaseFlowInputSchema.extend({
  textInput: z.string().describe('The full text content to analyze.'),
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Chapter-level metadata for the single chapter.
 * Based on SingleChapterStoryIdeaSchema but without title (uses project name for short-form).
 *
 * Note: keyEvents is NOT extracted here — for short-form projects, keyEvents is
 * copied from writtenPlot (extracted by plot-extractor-v2) to maintain a single
 * source of truth. See short-form-plot-keyevents-unification.md.
 */
const ShortStoryChapterSchema = z.object({
  povCharacterName: z
    .string()
    .optional()
    .describe('The name of the POV character, if identifiable from the text.'),
  povJustification: z
    .string()
    .optional()
    .describe('Explanation for why this character is the POV.'),
  chapterGoals: ChapterGoalsSchema.describe(
    'The narrative goals this story accomplishes (plot advancement, character development, stakes).'
  ),
  pacing: z
    .enum(['Slow & Reflective', 'Moderate & Developing', 'Fast & Action-Packed'])
    .describe('The overall pacing of the story.'),
  projectedWordCount: z
    .number()
    .describe('The estimated word count of the story based on analysis.'),
  scenes: z
    .array(SceneImportSchema)
    .describe('The scenes within this story, in chronological order.'),
});

/**
 * Output schema for single-chapter metadata extraction.
 */
export const OutputSchema = z.object({
  chapter: ShortStoryChapterSchema.describe(
    'Metadata for the single chapter that represents the entire short story.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
